import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Mail, Lock, Eye, EyeOff, LogIn, Users, Fingerprint, CalendarDays, CreditCard, BarChart3 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const features = [
  { icon: Users, label: "Socios" },
  { icon: Fingerprint, label: "Control de Accesos" },
  { icon: CalendarDays, label: "Membresías" },
  { icon: CreditCard, label: "Pagos" },
  { icon: BarChart3, label: "Reportes" },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@gymaccess.com");
  const [password, setPassword] = useState("admin123");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#050505] text-white">
      {/* ---------- LADO IZQUIERDO — PRESENTACIÓN ---------- */}
      <div className="hidden lg:flex relative w-[56%] flex-col justify-between p-12 xl:p-16 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/login-bg.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        {/* líneas geométricas de acento */}
        <div className="absolute -right-10 top-0 h-full w-[3px] rotate-[12deg] bg-gradient-to-b from-[#00f0ff]/0 via-[#00f0ff]/70 to-[#00f0ff]/0" />
        <div className="absolute right-8 top-0 h-full w-[6px] -rotate-[10deg] bg-gradient-to-b from-transparent via-[#FF7A1A]/60 to-transparent blur-[1px]" />

        <div className="relative">
          <BrandLogo size="h-24" data-testid="login-brand-logo" />
        </div>

        <div className="relative max-w-xl">
          <h1 className="font-display text-6xl xl:text-7xl leading-[0.92] tracking-wide">
            <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">CONTROL TOTAL</span>
            <br />
            <span className="text-[#00f0ff]" style={{ textShadow: "0 0 30px rgba(0,240,255,0.35)" }}>DE TU GIMNASIO</span>
          </h1>
          <div className="h-[3px] w-44 mt-5 bg-gradient-to-r from-[#00f0ff] via-[#00f0ff] to-[#FF7A1A]" />
          <p className="mt-6 text-base max-w-md leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
            Gestiona socios, membresías, pagos, productos y control de acceso desde una sola plataforma.
          </p>
          <p className="mt-2 text-sm max-w-md leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
            Gestión inteligente, acceso seguro y control biométrico integrado con dispositivos ZKTeco.
          </p>
        </div>

        <div className="relative flex flex-wrap gap-3">
          {features.map((f) => (
            <div key={f.label} className="flex items-center gap-2.5 bg-white/5 border border-white/10 backdrop-blur-sm px-3.5 py-2.5 rounded-lg">
              <span className="flex items-center justify-center w-8 h-8 rounded-md bg-[#00f0ff]/10 border border-[#00f0ff]/30">
                <f.icon className="w-4 h-4 text-[#00f0ff]" />
              </span>
              <span className="text-xs font-semibold text-white/80">{f.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- LADO DERECHO — PANEL DE LOGIN ---------- */}
      <div className="flex-1 flex items-center justify-center p-6 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] rounded-full bg-[#00f0ff]/10 blur-[120px] pointer-events-none" />

        <form onSubmit={submit} data-testid="login-form"
          className="relative w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 sm:p-10 shadow-[0_0_70px_-20px_rgba(0,240,255,0.35)]">
          <div className="absolute top-0 left-8 right-8 h-[2px] bg-gradient-to-r from-transparent via-[#00f0ff]/70 to-transparent" />

          <BrandLogo size="h-16" className="justify-center mb-6 lg:hidden" />

          <p className="overline text-[#00f0ff] mb-1.5">Panel de administración</p>
          <h2 className="font-display text-4xl tracking-wide">INICIAR SESIÓN</h2>
          <div className="h-[3px] w-28 mt-2 mb-7 bg-gradient-to-r from-[#00f0ff] to-[#FF7A1A]" />

          {error && (
            <div data-testid="login-error" className="mb-5 px-4 py-3 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-sm rounded-lg">
              {error}
            </div>
          )}

          <label className="overline block mb-2">Email</label>
          <div className="relative mb-5">
            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="login-email"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username"
              className="w-full h-12 bg-[#121212] border border-white/10 rounded-lg pl-11 pr-4 text-sm focus:border-[#00f0ff] focus:outline-none focus:ring-1 focus:ring-[#00f0ff]/40 transition-colors"
            />
          </div>

          <label className="overline block mb-2">Contraseña</label>
          <div className="relative mb-8">
            <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="login-password"
              type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
              className="w-full h-12 bg-[#121212] border border-white/10 rounded-lg pl-11 pr-11 text-sm focus:border-[#00f0ff] focus:outline-none focus:ring-1 focus:ring-[#00f0ff]/40 transition-colors"
            />
            <button
              type="button" data-testid="login-password-toggle" onClick={() => setShowPass((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#00f0ff] transition-colors p-1"
              aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <button
            data-testid="login-submit-button" disabled={loading}
            className="w-full h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-[#00f0ff] to-[#00b8d9] text-black font-bold uppercase tracking-wide rounded-lg hover:shadow-[0_0_30px_-4px_rgba(0,240,255,0.7)] hover:brightness-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Verificando..." : (<>Ingresar <LogIn className="w-4 h-4" /></>)}
          </button>

          <div className="mt-8 flex items-center justify-center gap-3 text-center">
            <span className="h-px w-8 bg-white/15" />
            <span className="overline text-[0.6rem] text-white/40">GLG GYM CONTROL · Tecnología · Control · Resultados</span>
            <span className="h-px w-8 bg-white/15" />
          </div>
        </form>
      </div>
    </div>
  );
}
